<template>
  <div v-if="showAdminModal" class="admin-modal" @click="closeModal">
    <div class="admin-card" @click.stop>
      <!-- 固定头部标题 -->
      <div class="admin-header">
        <h3>管理员控制中心</h3>
      </div>

      <!-- 可滚动内容区域 -->
      <div class="admin-content">
      <!-- 物品管理 -->
      <div class="section">
        <h4>添加物品</h4>
        <div class="input-group">
          <div class="item-input-container">
            <input
              v-model="itemName"
              @input="onItemNameInput"
              @focus="onItemFocus"
              @blur="hideSuggestions"
              @keydown="handleItemKeyDown"
              placeholder="物品名称"
              ref="itemNameInput"
            />
            <div v-if="showItemSuggestions && filteredItems.length > 0" class="item-suggestions">
              <div
                v-for="(item, index) in filteredItems"
                :key="item.name"
                :class="['item-suggestion', { active: index === selectedItemIndex }]"
                @mousedown="selectItem(item.name)"
              >
                {{ item.name }} ({{ item.description }})
              </div>
            </div>
          </div>
          <input v-model.number="itemCount" type="number" placeholder="数量" min="1" />
          <button @click="addItem">添加物品</button>
        </div>
      </div>

        <!-- 属性管理 -->
        <div class="section">
          <h4>设置属性</h4>

          <div class="input-group">
            <label>修为等级:</label>
            <input v-model.number="playerRealm" type="number" min="0" max="6" />
            <label>阶段:</label>
            <input v-model.number="playerSubstage" type="number" min="0" max="3" />
            <label>经验:</label>
            <input v-model.number="playerXp" type="number" min="0" />
            <button @click="setCultivation">设置修为</button>
          </div>

          <div class="input-group">
            <label>气血:</label>
            <input v-model.number="playerQi" type="number" min="0" />
            <label>最大气血:</label>
            <input v-model.number="playerMaxQi" type="number" min="0" />
            <button @click="setQi">设置气血</button>
          </div>

          <div class="input-group">
            <label>攻击:</label>
            <input v-model.number="playerAttack" type="number" min="0" />
            <label>防御:</label>
            <input v-model.number="playerDefense" type="number" min="0" />
            <button @click="setCombat">设置战斗属性</button>
          </div>

          <div class="input-group">
            <label>法力:</label>
            <input v-model.number="playerMana" type="number" min="0" />
            <label>神识强度:</label>
            <input v-model.number="playerDivineSense" type="number" min="0" />
            <button @click="setSpiritual">设置灵力属性</button>
          </div>

          <div class="input-group">
            <label>位置:</label>
            <input v-model="playerLocation" placeholder="位置名称" />
            <button @click="setLocation">设置位置</button>
          </div>
        </div>

        <!-- 技能管理 -->
        <div class="section">
          <h4>添加技能</h4>
          <div class="input-group">
            <input v-model="skillName" placeholder="技能名称" />
            <input v-model.number="skillLevel" type="number" placeholder="等级" min="1" />
            <button @click="addSkill">添加技能</button>
          </div>
        </div>

        <!-- 其他功能 -->
        <div class="section">
          <h4>其他功能</h4>
          <button @click="resetGame" class="danger">重置游戏</button>
          <button @click="saveGame">保存游戏</button>
        </div>
      </div>

      <!-- 固定底部关闭按钮 -->
      <div class="admin-footer">
        <button @click="closeModal">关闭</button>
      </div>
    </div>
  </div>
</template>

<script>
import gameController from './game-controller/GameController.js';
import { ITEMS } from './resources.js';
import { LOCATION_NAMES } from './exploration/exploration-data.js';

export default {
  name: 'AdminPanel',
  props: {
    showAdminModal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // 物品添加
      itemName: '',
      itemCount: 1,
      showItemSuggestions: false,
      filteredItems: [],
      selectedItemIndex: -1,

      // 属性设置
      playerRealm: 0,
      playerSubstage: 0,
      playerXp: 0,
      playerQi: 80,
      playerMaxQi: 80,
      playerAttack: 8,
      playerDefense: 4,
      playerMana: 40,
      playerDivineSense: 80,
      playerLocation: LOCATION_NAMES.UNKNOWN,

      // 技能添加
      skillName: '',
      skillLevel: 1,

      // 控制器是否准备好
      controllerReady: false
    };
  },

  watch: {
    showAdminModal(newVal) {
      if (newVal && !this.controllerReady) {
        // 如果面板打开但控制器还没准备好，延迟初始化
        this.initializeController();
      }
    }
  },

  mounted() {
    // 延迟初始化，避免与主应用初始化冲突
    this.$nextTick(() => {
      setTimeout(() => {
        this.initializeController();
      }, 200);
    });
  },

  methods: {
    initializeController() {
      try {
        // 检查控制器是否可用
        if (gameController && typeof gameController.getGameState === 'function') {
          this.controllerReady = true;
          this.refreshCurrentValues();
        } else {
          console.warn('GameController 尚未准备好，重试中...');
          setTimeout(() => this.initializeController(), 100);
        }
      } catch (error) {
        console.warn('初始化控制器失败:', error);
        setTimeout(() => this.initializeController(), 100);
      }
    },

    refreshCurrentValues() {
      if (!this.controllerReady) return;

      try {
        const state = gameController.getGameState();
        this.playerRealm = state.realm || 0;
        this.playerSubstage = state.substage || 0;
        this.playerXp = state.xp || 0;
        this.playerQi = state.qi || 80;
        this.playerMaxQi = state.maxQi || 80;
        this.playerAttack = state.attack || 8;
        this.playerDefense = state.defense || 4;
        this.playerMana = state.mana || 40;
        this.playerDivineSense = state.divineSenseStrength || 80;
        this.playerLocation = state.location || LOCATION_NAMES.UNKNOWN;
      } catch (error) {
        console.warn('无法获取游戏状态，使用默认值:', error);
      }
    },

    closeModal() {
      this.$emit('close-modal');
    },

    onItemFocus() {
      this.showItemSuggestions = true;
      // 聚焦时显示所有物品
      this.filteredItems = Object.values(ITEMS);
      this.selectedItemIndex = -1;
    },

    onItemNameInput() {
      if (!this.itemName.trim()) {
        this.filteredItems = [];
        return;
      }

      // 过滤匹配的物品
      const query = this.itemName.toLowerCase();
      this.filteredItems = Object.values(ITEMS).filter(item =>
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query)
      ).slice(0, 10); // 限制显示10个结果

      this.selectedItemIndex = -1;
    },

    hideSuggestions() {
      // 延迟隐藏，以便点击选择生效
      setTimeout(() => {
        this.showItemSuggestions = false;
        this.selectedItemIndex = -1;
      }, 150);
    },

    handleItemKeyDown(event) {
      if (!this.showItemSuggestions || this.filteredItems.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.selectedItemIndex = Math.min(this.selectedItemIndex + 1, this.filteredItems.length - 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.selectedItemIndex = Math.max(this.selectedItemIndex - 1, -1);
          break;
        case 'Enter':
          event.preventDefault();
          if (this.selectedItemIndex >= 0) {
            this.selectItem(this.filteredItems[this.selectedItemIndex].name);
          }
          break;
        case 'Escape':
          this.showItemSuggestions = false;
          this.selectedItemIndex = -1;
          break;
      }
    },

    selectItem(itemName) {
      this.itemName = itemName;
      this.showItemSuggestions = false;
      this.selectedItemIndex = -1;
      // 聚焦到数量输入框
      this.$nextTick(() => {
        const quantityInput = this.$el.querySelector('input[type="number"]');
        if (quantityInput) quantityInput.focus();
      });
    },

    addItem() {
      if (!this.controllerReady) {
        alert('系统尚未准备好，请稍后再试');
        return;
      }

      if (!this.itemName.trim()) {
        alert('请输入物品名称');
        return;
      }

      if (this.itemCount <= 0) {
        alert('数量必须大于0');
        return;
      }

      // 检查物品是否存在
      if (!ITEMS[this.itemName]) {
        alert('物品不存在，请检查名称');
        return;
      }

      try {
        gameController.stateManager.addItemToBag(this.itemName, this.itemCount);
        alert(`成功添加 ${this.itemCount} 个 ${this.itemName}`);
        this.itemName = '';
        this.itemCount = 1;
      } catch (error) {
        alert('添加物品失败：' + error.message);
      }
    },

    setCultivation() {
      if (!this.controllerReady) {
        alert('系统尚未准备好，请稍后再试');
        return;
      }

      try {
        gameController.stateManager.updateAttribute('realm', this.playerRealm);
        gameController.stateManager.updateAttribute('substage', this.playerSubstage);
        gameController.stateManager.updateAttribute('xp', this.playerXp);
        alert('修为设置成功');
      } catch (error) {
        alert('设置修为失败：' + error.message);
      }
    },

    setQi() {
      if (!this.controllerReady) {
        alert('系统尚未准备好，请稍后再试');
        return;
      }

      try {
        gameController.stateManager.updateAttribute('qi', this.playerQi);
        gameController.stateManager.updateAttribute('maxQi', this.playerMaxQi);
        alert('气血设置成功');
      } catch (error) {
        alert('设置气血失败：' + error.message);
      }
    },

    setCombat() {
      if (!this.controllerReady) {
        alert('系统尚未准备好，请稍后再试');
        return;
      }

      try {
        gameController.stateManager.updateAttribute('attack', this.playerAttack);
        gameController.stateManager.updateAttribute('defense', this.playerDefense);
        alert('战斗属性设置成功');
      } catch (error) {
        alert('设置战斗属性失败：' + error.message);
      }
    },

    setSpiritual() {
      if (!this.controllerReady) {
        alert('系统尚未准备好，请稍后再试');
        return;
      }

      try {
        gameController.stateManager.updateAttribute('mana', this.playerMana);
        gameController.stateManager.updateAttribute('divineSenseStrength', this.playerDivineSense);
        alert('灵力属性设置成功');
      } catch (error) {
        alert('设置灵力属性失败：' + error.message);
      }
    },

    setLocation() {
      if (!this.controllerReady) {
        alert('系统尚未准备好，请稍后再试');
        return;
      }

      if (!this.playerLocation.trim()) {
        alert('请输入位置名称');
        return;
      }

      try {
        gameController.stateManager.setLocation(this.playerLocation);
        alert('位置设置成功');
      } catch (error) {
        alert('设置位置失败：' + error.message);
      }
    },

    addSkill() {
      if (!this.controllerReady) {
        alert('系统尚未准备好，请稍后再试');
        return;
      }

      if (!this.skillName.trim()) {
        alert('请输入技能名称');
        return;
      }

      if (this.skillLevel <= 0) {
        alert('技能等级必须大于0');
        return;
      }

      try {
        const state = gameController.getGameState();
        if (!state.skills) {
          state.skills = {};
        }
        state.skills[this.skillName] = this.skillLevel;
        gameController.stateManager.updateState({ skills: state.skills });
        alert(`成功添加技能 ${this.skillName} (等级 ${this.skillLevel})`);
        this.skillName = '';
        this.skillLevel = 1;
      } catch (error) {
        alert('添加技能失败：' + error.message);
      }
    },

    resetGame() {
      if (!this.controllerReady) {
        alert('系统尚未准备好，请稍后再试');
        return;
      }

      if (confirm('确定要重置游戏吗？这将清除所有进度！')) {
        try {
          gameController.resetGame();
          alert('游戏已重置');
          this.closeModal();
        } catch (error) {
          alert('重置游戏失败：' + error.message);
        }
      }
    },

    saveGame() {
      if (!this.controllerReady) {
        alert('系统尚未准备好，请稍后再试');
        return;
      }

      try {
        gameController.saveGame();
        alert('游戏已保存');
      } catch (error) {
        alert('保存游戏失败：' + error.message);
      }
    }
  }
};
</script>

<style scoped>
.admin-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.admin-card {
  background: #1a1a1a;
  color: #e6f3d6;
  border-radius: 8px;
  border: 1px solid #333;
  max-width: 600px;
  max-height: 85vh;
  width: 95%;
  display: flex;
  flex-direction: column;
}

.admin-header {
  padding: 15px 15px 5px 15px;
  flex-shrink: 0;
}

.admin-header h3 {
  margin: 0 0 10px 0;
  color: #ffd166;
  text-align: center;
  font-size: 18px;
}

.admin-content {
  padding: 5px 15px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.admin-footer {
  padding: 15px;
  flex-shrink: 0;
  text-align: center;
  border-top: 1px solid #333;
}

.admin-footer button {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  background: #6b7280;
  color: white;
  cursor: pointer;
  font-size: 14px;
}

.admin-footer button:hover {
  background: #4b5563;
}

.section {
  margin-bottom: 15px;
  padding: 12px;
  background: #2a2a2a;
  border-radius: 6px;
}

.section h4 {
  margin: 0 0 8px 0;
  color: #60a5fa;
  font-size: 16px;
  font-weight: 600;
}

.input-group {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.input-group label {
  min-width: 70px;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
}

.input-group input {
  padding: 4px 6px;
  border: 1px solid #555;
  border-radius: 4px;
  background: #333;
  color: #e6f3d6;
  flex: 1;
  min-width: 80px;
  font-size: 14px;
}

.input-group input[type="number"] {
  width: 70px;
  flex: none;
}

.input-group button {
  padding: 4px 10px;
  border: none;
  border-radius: 4px;
  background: #0ea5a2;
  color: #042f2e;
  cursor: pointer;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
}

.input-group button:hover {
  background: #0891a2;
}

.danger {
  background: #dc2626 !important;
  color: white !important;
}

.danger:hover {
  background: #b91c1c !important;
}

.actions {
  text-align: center;
  margin-top: 20px;
}

.actions button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background: #6b7280;
  color: white;
  cursor: pointer;
}

.actions button:hover {
  background: #4b5563;
}

/* 物品输入框和建议列表样式 */
.item-input-container {
  position: relative;
  flex: 1;
  min-width: 200px;
}

.item-input-container input {
  width: 100%;
  padding: 5px 8px;
  border: 1px solid #555;
  border-radius: 4px;
  background: #333;
  color: #e6f3d6;
}

.item-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #2a2a2a;
  border: 1px solid #555;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.item-suggestion {
  padding: 8px 12px;
  cursor: pointer;
  color: #e6f3d6;
  border-bottom: 1px solid #444;
  font-size: 14px;
  line-height: 1.4;
}

.item-suggestion:last-child {
  border-bottom: none;
}

.item-suggestion:hover,
.item-suggestion.active {
  background: #3a3a3a;
  color: #ffd166;
}

.item-suggestion.active {
  background: #4a4a4a;
}

/* 移动端优化 */
@media (max-width: 768px) {
  .admin-modal {
    padding: 10px;
  }

  .admin-card {
    width: 100%;
    max-height: 90vh;
    border-radius: 6px;
  }

  .admin-header {
    padding: 12px 12px 4px 12px;
  }

  .admin-header h3 {
    font-size: 16px;
    margin-bottom: 8px;
  }

  .admin-content {
    padding: 4px 12px;
  }

  .admin-footer {
    padding: 12px;
  }

  .admin-footer button {
    padding: 5px 10px;
    font-size: 13px;
  }

  .section {
    margin-bottom: 12px;
    padding: 10px;
    border-radius: 4px;
  }

  .section h4 {
    font-size: 15px;
    margin-bottom: 6px;
  }

  .input-group {
    gap: 6px;
    margin-bottom: 6px;
    align-items: stretch;
  }

  .input-group label {
    min-width: 60px;
    font-size: 13px;
    margin-bottom: 2px;
    display: block;
  }

  .input-group input {
    padding: 3px 4px;
    font-size: 13px;
    min-width: 60px;
  }

  .input-group input[type="number"] {
    width: 60px;
  }

  .input-group button {
    padding: 3px 8px;
    font-size: 13px;
    white-space: normal;
    min-height: 32px;
  }

  /* 移动端堆叠布局 */
  .input-group {
    flex-direction: column;
    align-items: stretch;
  }

  .input-group label {
    align-self: flex-start;
    margin-bottom: 4px;
  }

  .item-input-container {
    min-width: unset;
    margin-bottom: 4px;
  }

  .item-input-container input {
    padding: 3px 4px;
    font-size: 13px;
  }

  .item-suggestions {
    max-height: 150px;
  }

  .item-suggestion {
    padding: 6px 8px;
    font-size: 13px;
  }
}

/* 小屏幕设备进一步优化 */
@media (max-width: 480px) {
  .admin-card {
    max-height: 95vh;
  }

  .admin-header h3 {
    font-size: 15px;
  }

  .section h4 {
    font-size: 14px;
  }

  .input-group input {
    font-size: 12px;
  }

  .input-group button {
    font-size: 12px;
    padding: 2px 6px;
  }

  .item-suggestion {
    padding: 4px 6px;
    font-size: 12px;
  }
}
</style>
